import { compact } from "lodash";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  ArtifactType,
  CreatedArtifactFragmentFragment,
  GoalScope,
} from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import ArtifactCreationDialog from "@apps/artifact-creation-dialog/artifact-creation-dialog";
import { refreshAlignmentOfGoalIds } from "@apps/goal-alignment/helpers";
import ShowSidebarButton from "@apps/main/components/show-sidebar-button";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar, currentUserVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import { useLink } from "@components/link/link-helpers";
import {
  ToggleButtonGroup,
  ToggleButtonGroupTheme,
  ToggleButtonGroupType,
} from "@components/toggle-button-group/toggle-button-group";
import {
  UserComboboxOption,
  UserComboboxOptionType,
} from "@components/user-combobox/user-combobox-list";
import { isGoalArtifactNode } from "@helpers/helpers";

import getOverviewGoalsQuery from "./graphql/get-overview-goals-query";

enum ToggleButtonEnum {
  overview = "Overview",
  alignment = "Alignment",
  kpis = "KPIs",
}

const GoalPageHeader = ({
  selected,
}: {
  selected?: UserComboboxOption | null;
}) => {
  const link = useLink();
  const label = useLabel();
  const location = useLocation<TFLocationState>();
  const currentUser = currentUserVar();
  const currentOrganization = currentOrganizationVar();
  const [isShowingCreateDialog, setIsShowingCreateDialog] = useState(false);

  const handleClickToggle = (
    button: ToggleButtonGroupType<ToggleButtonEnum>,
  ) => {
    if (button.value === ToggleButtonEnum.alignment) {
      link.redirect(`/goal-alignment`);
    } else if (button.value === ToggleButtonEnum.kpis) {
      link.redirect(`/kpis`);
    } else {
      link.redirect(`/goals`);
    }
  };

  const handleCloseCreateDialog = (
    createdArtifact?: CreatedArtifactFragmentFragment,
  ) => {
    setIsShowingCreateDialog(false);
    if (createdArtifact && isGoalArtifactNode(createdArtifact)) {
      refreshAlignmentOfGoalIds([
        createdArtifact.id,
        createdArtifact.parentGoal?.id,
      ]);
    }
  };

  const isKpiPage = location.pathname.startsWith("/kpis");

  return (
    <div className="relative z-horizontalNavbar shrink-0 px-6 tf-print-display-none h-14 border-b flex items-center bg-content-background">
      {isShowingCreateDialog && (
        <ArtifactCreationDialog
          hideTypeSelector
          formOptions={{
            artifactType: ArtifactType.Goal,
            owners:
              selected?.type === UserComboboxOptionType.USER
                ? [
                    {
                      id: selected.id,
                      name: selected.name,
                    },
                  ]
                : [currentUser],
            scope:
              selected?.type === UserComboboxOptionType.TEAM
                ? GoalScope.Team
                : selected?.type === UserComboboxOptionType.ORG
                  ? GoalScope.Organization
                  : GoalScope.Personal,
            teams:
              selected?.type === UserComboboxOptionType.TEAM
                ? [{ id: selected.id, title: selected.title }]
                : [],
          }}
          openSidebarAfterCreation
          refetchQueries={[getOverviewGoalsQuery]}
          onClose={handleCloseCreateDialog}
        />
      )}
      <header className="w-full flex gap-4 justify-between items-center">
        <div className="flex gap-2 items-center text-sm text-gray-600 min-w-0">
          <ShowSidebarButton />
          <Button theme={buttonTheme.text} to="/goals" size={ButtonSize.medium}>
            {label("goal", { titleCase: true, pluralize: true })}
          </Button>
          <ToggleButtonGroup<ToggleButtonEnum>
            theme={ToggleButtonGroupTheme.roundedCompact}
            buttons={compact([
              {
                title: "Overview",
                active: location.pathname.startsWith("/goals"),
                value: ToggleButtonEnum.overview,
              },
              {
                title: "Alignment",
                active: location.pathname.startsWith("/goal-alignment"),
                value: ToggleButtonEnum.alignment,
              },
              currentOrganization.featureFlags.kpis && {
                title: "KPIs",
                active: location.pathname.startsWith("/kpis"),
                value: ToggleButtonEnum.kpis,
              },
            ])}
            onClick={handleClickToggle}
          />
        </div>
        <div className="flex items-center gap-4 shrink-0 empty:hidden">
          {isKpiPage ? (
            <>
              <Button
                theme={buttonTheme.default}
                size={ButtonSize.medium}
                to="/kpis/group/new"
                text="Add KPI Group"
              />
              <Button
                theme={buttonTheme.primary}
                size={ButtonSize.medium}
                to="/kpis/new"
                text="Add KPI"
              />
            </>
          ) : (
            <Button
              theme={buttonTheme.primary}
              size={ButtonSize.medium}
              text={`Create ${label("goal")}`}
              onClick={() => setIsShowingCreateDialog(true)}
            />
          )}
        </div>
      </header>
    </div>
  );
};

export default GoalPageHeader;
